export type Theme = "default" | "dark" | "dark-alt" | "light" | "color" | "brand" | "brand-alt" | "chartreuse-100" | "header" | "form-input" | "footer" | "light-grey" | "light-grey-75" | "cool-grey-40" | "sand-200" | "mid-green" | "grey-400" | "grey-500" | "mid-blue" | "light-blue" | "blue" | "pink" | "orange" | "green" | "red" | "product-card" | "inherit" | "green-light";
export type ThemeWithRepeatable = Theme | "repeatable";

export interface IThemeConfig {
  prefix: string;
  selector: string;
}

type Themes = {
  [key in Theme]: IThemeConfig;
};

export type ThemeMap = {
  [key in Theme]: string;
};

export const themeRootClassMap: ThemeMap = {
  default: "bg-transparent",
  dark: "bg-black",
  "dark-alt": "bg-black",
  light: "bg-white",
  brand: "bg-brand",
  "brand-alt": "bg-brand",
  "chartreuse-100": "bg-chartreuse-100",
  color: "bg-pink-700", // This no longer works as we override default colours in the tailwind config
  header: "bg-white",
  footer: "bg-black",
  "light-blue": "bg-light-blue",
  "mid-blue": "bg-mid-blue",
  blue: "bg-blue",
  pink: "bg-pink",
  orange: "bg-orange",
  green: "bg-green",
  red: "bg-red",
  "light-grey": "bg-light-grey",
  "light-grey-75": "bg-light-grey-75",
  "cool-grey-40": "bg-cool-grey-40",
  "sand-200": "bg-sand-200",
  "green-light": "bg-green-light",
  "mid-green": "bg-mid-green",
  "grey-400": "bg-grey-400",
  "grey-500": "bg-grey-500",
  "form-input": "bg-transparent",
  "product-card": "bg-white hover:bg-black transition transition-color ease-in-out duration-300",
  inherit: "bg-inherit",
};

const PSEUDO_THEME_ROOT_BLACK = "before:bg-black";
export const pseudoThemeRootClassMap: ThemeMap = {
  default: "before:bg-transparent",
  dark: PSEUDO_THEME_ROOT_BLACK,
  "dark-alt": PSEUDO_THEME_ROOT_BLACK,
  light: "before:bg-white",
  brand: "before:bg-brand",
  "brand-alt": "before:bg-brand",
  "chartreuse-100": "before:bg-chartreuse-100",
  color: "before:bg-pink-700",
  header: "before:bg-white",
  footer: PSEUDO_THEME_ROOT_BLACK,
  blue: "before:bg-blue",
  "light-blue": "before:bg-light-blue",
  "mid-blue": "before:bg-mid-blue",
  pink: "before:bg-pink",
  orange: "before:bg-orange",
  green: "before:bg-green",
  red: "before:bg-red",
  "light-grey": "before:bg-light-grey",
  "light-grey-75": "before:bg-light-grey-75",
  "cool-grey-40": "before:bg-cool-grey-40",
  "sand-200": "before:bg-sand-200",
  "mid-green": "before:bg-mid-green",
  "green-light": "before:bg-green-light",
  "grey-400": "before:bg-grey-400",
  "grey-500": "before:bg-grey-500",
  "form-input": "before:bg-transparent",
  "product-card": "before:bg-white hover:before:bg-black",
  inherit: "before:bg-inherit",
};

/*
  Currently themes has to be repeated in tailwind.config.js until I
  have time to work out how to import into the tailwind config correctly
*/
export const themes: Themes = {
  default: {
    prefix: "theme-default",
    selector: "[data-theme=\"default\"] &",
  },
  dark: {
    prefix: "theme-dark",
    selector: "[data-theme=\"dark\"] &",
  },
  "dark-alt": {
    prefix: "theme-dark-alt",
    selector: "[data-theme=\"dark-alt\"] &",
  },
  light: {
    prefix: "theme-light",
    selector: "[data-theme=\"light\"] &",
  },
  color: {
    prefix: "theme-color",
    selector: "[data-theme=\"color\"] &",
  },
  brand: {
    prefix: "theme-brand",
    selector: "[data-theme=\"brand\"] &",
  },
  "brand-alt": {
    prefix: "theme-brand-alt",
    selector: "[data-theme=\"brand-alt\"] &",
  },
  "chartreuse-100": {
    prefix: "theme-chartreuse-100",
    selector: "[data-theme=\"chartreuse-100\"] &",
  },
  header: {
    prefix: "theme-header",
    selector: "[data-theme=\"header\"] &",
  },
  footer: {
    prefix: "theme-footer",
    selector: "[data-theme=\"footer\"] &",
  },
  blue: {
    prefix: "theme-blue",
    selector: "[data-theme=\"blue\"] &",
  },
  "light-blue": {
    prefix: "theme-light-blue",
    selector: "[data-theme=\"light-blue\"] &",
  },
  "mid-blue": {
    prefix: "theme-mid-blue",
    selector: "[data-theme=\"mid-blue\"] &",
  },
  pink: {
    prefix: "theme-pink",
    selector: "[data-theme=\"pink\"] &",
  },
  orange: {
    prefix: "theme-orange",
    selector: "[data-theme=\"orange\"] &",
  },
  red: {
    prefix: "theme-red",
    selector: "[data-theme=\"red\"] &",
  },
  green: {
    prefix: "theme-green",
    selector: "[data-theme=\"green\"] &",
  },
  "light-grey": {
    prefix: "theme-light-grey",
    selector: "[data-theme=\"light-grey\"] &",
  },
  "light-grey-75": {
    prefix: "theme-light-grey-75",
    selector: "[data-theme=\"light-grey-75\"] &",
  },
  "cool-grey-40": {
    prefix: "theme-cool-grey-40",
    selector: "[data-theme=\"cool-grey-40\"] &",
  },
  "sand-200": {
    prefix: "theme-sand-200",
    selector: "[data-theme=\"sand-200\"] &",
  },
  "mid-green": {
    prefix: "theme-mid-green",
    selector: "[data-theme=\"mid-green\"] &",
  },
  "green-light": {
    prefix: "theme-green-light",
    selector: "[data-theme=\"green-light\"] &",
  },
  "grey-400": {
    prefix: "theme-grey-400",
    selector: "[data-theme=\"grey-400\"] &",
  },
  "grey-500": {
    prefix: "theme-grey-500",
    selector: "[data-theme=\"grey-500\"] &",
  },
  "form-input": {
    prefix: "theme-form-input",
    selector: "[data-theme=\"form-input\"] &",
  },
  "product-card": {
    prefix: "theme-product-card",
    selector: "[data-theme=\"product-card\"] &",
  },
  inherit: {
    prefix: "theme-inherit",
    selector: "[data-theme=\"inherit\"] &",
  },
};
